import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';

const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
`;

const SectionHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 14px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;

const StatCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 14px;
  background: #f9fafb;
`;

const StatLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
`;

const StatValue = styled.div`
  margin-top: 4px;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
`;

const StatHint = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
`;

const ProgressCard = styled.div`
  margin-top: 16px;
  border-radius: 16px;
  padding: 16px;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const ProgressTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const ProgressFigure = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1d4ed8;
`;

const ProgressTrack = styled.div`
  position: relative;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #22c55e, #16a34a);
  width: ${(p) => `${Math.min(p.$value || 0, 100)}%`};
  transition: width 0.3s ease;
`;

const EmptyState = styled.div`
  padding: 32px;
  border: 2px dashed #dbeafe;
  border-radius: 16px;
  text-align: center;
  color: #0f172a;
  background: #eff6ff;
`;

const EmptyTitle = styled.div`
  font-weight: 700;
  font-size: 18px;
`;

const EmptyText = styled.p`
  margin: 8px 0 0;
  color: #475569;
  font-size: 14px;
`;

const ErrorState = styled(EmptyState)`
  border-color: #fee2e2;
  background: #fef2f2;
  color: #991b1b;
`;

const SubjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const SubjectCard = styled.div`
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.1s ease, border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);
    border-color: #c7d2fe;
    background: #f9fafb;
  }

  &.is-selected {
    border-color: #4f46e5;
    box-shadow: 0 0 0 1px rgba(79, 70, 229, 0.3);
    background: #eef2ff;
  }
`;

const SubjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
`;

const SubjectTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const SubjectCode = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const SubjectPercentage = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1d4ed8;
`;

const SubjectMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const BadgeRow = styled.div`
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Badge = styled.span`
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  color: #374151;
  background: #f3f4f6;
`;

const SessionsWrapper = styled.div`
  margin-top: 8px;
`;

const SessionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
  color: #4b5563;
`;

const FilterBar = styled.div`
  margin-bottom: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FilterChip = styled.button`
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? '#4f46e5' : '#e5e7eb')};
  background: ${(p) => (p.$active ? '#eef2ff' : '#ffffff')};
  color: ${(p) => (p.$active ? '#312e81' : '#4b5563')};
  font-size: 12px;
  padding: 5px 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: #4f46e5;
  }
`;

const SessionTable = styled.div`
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  background: #ffffff;
`;

const SessionRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1.5fr 1fr 1fr 0.9fr 0.9fr;
  gap: 8px;
  padding: 8px 12px;
  font-size: 13px;
  align-items: center;
  border-top: 1px solid #f3f4f6;

  &:first-child {
    border-top: none;
  }
`;

const SessionHeaderRow = styled(SessionRow)`
  background: #f9fafb;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
`;

const DateHeading = styled.div`
  margin: 12px 4px 4px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
`;

const StatusPill = styled.span`
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => p.$bg || '#e5e7eb'};
  color: ${(p) => p.$color || '#374151'};
`;
const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 16px;
`;

const ModalContainer = styled.div`
  width: 100%;
  max-width: 980px;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.45);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const ModalSubtitle = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

const ModalHeaderText = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`;

const ModalBody = styled.div`
  padding: 14px 20px 18px;
  overflow: auto;
`;

const SubjectDetailCounts = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`;

const SubjectDetailColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-top: 12px;
`;

const formatDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatTime = (value) => {
  if (!value) return null;
  const [hourRaw, minuteRaw] = String(value).split(':');
  const hourNum = parseInt(hourRaw, 10);
  const minuteNum = parseInt(minuteRaw || '0', 10);
  if (Number.isNaN(hourNum)) return value;

  const period = hourNum >= 12 ? 'PM' : 'AM';
  const hour12 = ((hourNum + 11) % 12) + 1;
  const hourStr = String(hour12).padStart(2, '0');
  const minuteStr = String(Number.isNaN(minuteNum) ? 0 : minuteNum).padStart(2, '0');

  return `${hourStr}:${minuteStr} ${period}`;
};

const formatPercentage = (value) => {
  if (value === null || value === undefined) return '0%';
  const num = Number(value);
  if (!Number.isFinite(num)) return '0%';
  return `${Math.round(num * 10) / 10}%`;
};

const statusPalette = {
  Present: { bg: '#dcfce7', color: '#166534', label: 'Present' },
  Absent: { bg: '#fee2e2', color: '#b91c1c', label: 'Absent' },
  Late: { bg: '#fef3c7', color: '#b45309', label: 'Late' },
  Excused: { bg: '#e0f2fe', color: '#0369a1', label: 'Excused' },
};

export default function Attendance() {
  const [summary, setSummary] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [subjectFilterId, setSubjectFilterId] = useState(null);

  useEffect(() => {
    const user = AuthService.getUser();
    const registration = user?.selectedRegistration || null;
    const studentId = user?.studentId || user?.student?.studentId || null;

    if (!studentId || !registration?.registrationNumber) {
      setError('Please select a course to view attendance.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const studentIdEnc = encodeURIComponent(studentId);
    const regNoEnc = encodeURIComponent(registration.registrationNumber);

    Promise.all([
      axiosInstance.get(`/students/${studentIdEnc}/attendance/${regNoEnc}`),
      axiosInstance.get(`/students/${studentIdEnc}/attendance/${regNoEnc}/subjects`),
      axiosInstance.get(`/students/${studentIdEnc}/attendance`),
    ])
      .then(([summaryResp, subjectsResp, sessionsResp]) => {
        if (cancelled) return;
        const subjectList = Array.isArray(subjectsResp?.data?.subjectWise)
          ? subjectsResp.data.subjectWise
          : [];

        // Use backend attendanceSummary as the source of truth for overall stats
        setSummary(summaryResp?.data?.attendanceSummary || null);
        setSubjects(subjectList);

        const allSessions = Array.isArray(sessionsResp?.data?.attendance)
          ? sessionsResp.data.attendance
          : [];
        const filtered = allSessions.filter((s) =>
          !registration.registrationNumber || s.registrationNumber === registration.registrationNumber
        );

        // Deduplicate sessions by date, subject, hall, and lecturer.
        // If duplicates exist (even with different times), prefer the one marked as Present.
        const dedupedMap = new Map();
        filtered.forEach((session) => {
          const dateKey = session.date ? new Date(session.date).toISOString().slice(0, 10) : String(session.date || '');
          const subjectKey = String(session.subjectName || '');
          const roomKey = String(session.roomName || '');
          const lecturerKey = String(session.lecturerName || '');
          const key = `${dateKey}|${subjectKey}|${roomKey}|${lecturerKey}`;

          const existing = dedupedMap.get(key);
          if (!existing) {
            dedupedMap.set(key, session);
          } else {
            const isCurrentPresent = session.status === 'Present';
            const isExistingPresent = existing.status === 'Present';
            // If one of the duplicates is Present, keep the Present one.
            if (isCurrentPresent && !isExistingPresent) {
              dedupedMap.set(key, session);
            }
          }
        });

        const dedupedSessions = Array.from(dedupedMap.values());

        // Sort sessions by date (newest first), then by start time where available,
        // so that views show attendance from the current date back into the past.
        dedupedSessions.sort((a, b) => {
          const dateA = a.date ? new Date(a.date) : null;
          const dateB = b.date ? new Date(b.date) : null;

          const isValidA = dateA && !Number.isNaN(dateA.getTime());
          const isValidB = dateB && !Number.isNaN(dateB.getTime());

          if (isValidA && isValidB && dateA.getTime() !== dateB.getTime()) {
            // Newest date first
            return dateB.getTime() - dateA.getTime();
          }

          if (!isValidA && isValidB) return 1;
          if (isValidA && !isValidB) return -1;

          // Same (or invalid) date – order by start time if present
          const timeToMinutes = (value) => {
            if (!value) return -1;
            const [h, m] = String(value).split(':');
            const hours = Number.parseInt(h, 10);
            const minutes = Number.parseInt(m || '0', 10);
            if (Number.isNaN(hours)) return -1;
            return hours * 60 + (Number.isNaN(minutes) ? 0 : minutes);
          };

          const tA = timeToMinutes(a.startTime);
          const tB = timeToMinutes(b.startTime);

          if (tA !== tB) return tA - tB;

          return 0;
        });

        setSessions(dedupedSessions);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load attendance', err);
        const msg =
          err?.response?.data?.error || err?.response?.data?.message || "We couldn't load attendance right now.";
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const totalPercentage = useMemo(() => {
    return summary?.attendancePercentage ?? 0;
  }, [summary]);

  const subjectFilterOptions = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(sessions)) return [];

    sessions.forEach((session) => {
      const batchSubjectId = session.batchSubjectId;
      if (!batchSubjectId) return;

      const key = String(batchSubjectId);
      const existing = map.get(key) || {
        id: key,
        label: session.subjectName || 'Subject',
        count: 0,
      };
      existing.count += 1;
      map.set(key, existing);
    });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [sessions]);

  const filteredSessions = useMemo(() => {
    if (!Array.isArray(sessions)) return [];
    if (!subjectFilterId) return sessions;
    return sessions.filter((session) => String(session.batchSubjectId) === String(subjectFilterId));
  }, [sessions, subjectFilterId]);

  const groupedSessions = useMemo(() => {
    if (!Array.isArray(filteredSessions) || filteredSessions.length === 0) return [];

    const groups = new Map();

    filteredSessions.forEach((session) => {
      const dateObj = session.date ? new Date(session.date) : null;
      const key = dateObj && !Number.isNaN(dateObj.getTime())
        ? dateObj.toISOString().slice(0, 10)
        : String(session.date || '');
      const label = formatDate(session.date);

      if (!groups.has(key)) {
        groups.set(key, { key, label, items: [] });
      }
      groups.get(key).items.push(session);
    });

    return Array.from(groups.values());
  }, [filteredSessions]);

  // Derive per-subject statistics from the same deduplicated sessions that power
  // the "All attendance entries" view. This ensures that the subject-wise
  // Total / Present / Absent counts match the detailed schedule list.
  const subjectStatsFromSessions = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(sessions)) return map;

    sessions.forEach((session) => {
      const batchSubjectId = session.batchSubjectId;
      if (!batchSubjectId) return;

      const key = String(batchSubjectId);
      const current = map.get(key) || { total: 0, present: 0, absent: 0 };

      current.total += 1;
      if (session.status === 'Present') current.present += 1;
      else if (session.status === 'Absent') current.absent += 1;

      map.set(key, current);
    });

    return map;
  }, [sessions]);

  // Fallback map keyed by normalized subject name, in case the
  // batchSubjectId in the summary payload does not match the
  // batchSubjectId in the schedule-based attendance list.
  const subjectStatsFromSessionsByName = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(sessions)) return map;

    sessions.forEach((session) => {
      const rawName = session.subjectName;
      if (!rawName) return;

      const key = String(rawName).trim().toLowerCase();
      if (!key) return;

      const current = map.get(key) || { total: 0, present: 0, absent: 0 };

      current.total += 1;
      if (session.status === 'Present') current.present += 1;
      else if (session.status === 'Absent') current.absent += 1;

      map.set(key, current);
    });

    return map;
  }, [sessions]);

  const selectedSubject = useMemo(() => {
    if (!selectedSubjectId) return null;
    return subjects.find((s) => String(s.batchSubjectId) === String(selectedSubjectId)) || null;
  }, [subjects, selectedSubjectId]);

  const selectedSubjectSessions = useMemo(() => {
    if (!selectedSubjectId || !selectedSubject) return [];

    const idMatches = sessions.filter(
      (session) => String(session.batchSubjectId) === String(selectedSubject.batchSubjectId)
    );
    if (idMatches.length > 0) return idMatches;

    const subjectNameKey = String(selectedSubject.subjectName || '')
      .trim()
      .toLowerCase();
    if (!subjectNameKey) return [];

    return sessions.filter((session) => {
      const rawName = session.subjectName;
      if (!rawName) return false;
      return String(rawName).trim().toLowerCase() === subjectNameKey;
    });
  }, [sessions, selectedSubjectId, selectedSubject]);

  const selectedSubjectGroupedSessions = useMemo(() => {
    if (!Array.isArray(selectedSubjectSessions) || selectedSubjectSessions.length === 0) return [];

    const groups = new Map();

    selectedSubjectSessions.forEach((session) => {
      const dateObj = session.date ? new Date(session.date) : null;
      const key = dateObj && !Number.isNaN(dateObj.getTime())
        ? dateObj.toISOString().slice(0, 10)
        : String(session.date || '');
      const label = formatDate(session.date);

      if (!groups.has(key)) {
        groups.set(key, { key, label, items: [] });
      }
      groups.get(key).items.push(session);
    });

    return Array.from(groups.values());
  }, [selectedSubjectSessions]);

  const selectedSubjectPresentSessions = useMemo(() => {
    return selectedSubjectSessions.filter((session) => session.status === 'Present');
  }, [selectedSubjectSessions]);

  const selectedSubjectAbsentSessions = useMemo(() => {
    return selectedSubjectSessions.filter((session) => session.status === 'Absent');
  }, [selectedSubjectSessions]);

  const selectedSubjectStats = useMemo(() => {
    if (!selectedSubjectId) return null;
    const key = String(selectedSubjectId);
    const statsFromSessions = subjectStatsFromSessions.get(key);
    if (statsFromSessions) return statsFromSessions;

    const fallbackSubject = subjects.find((s) => String(s.batchSubjectId) === key);
    if (!fallbackSubject) return null;

    const nameKey = String(fallbackSubject.subjectName || '')
      .trim()
      .toLowerCase();
    const statsByName = nameKey ? subjectStatsFromSessionsByName.get(nameKey) : null;
    if (statsByName) return statsByName;

    return {
      total: fallbackSubject.totalClasses || 0,
      present: fallbackSubject.attendedClasses || 0,
      absent: fallbackSubject.absentClasses || 0,
    };
  }, [selectedSubjectId, subjectStatsFromSessions, subjectStatsFromSessionsByName, subjects]);

  const title = 'Attendance overview';

  return (
    <StudentLayout title={title}>
      <Wrapper>
        <Section>
          <SectionHeading>
            <Title>Attendance summary</Title>
            <Subtitle>
              Overall attendance for your selected course, with a breakdown by subject and individual sessions.
            </Subtitle>
          </SectionHeading>

          {loading && <Loader message="Loading attendance data…" />}

          {!loading && error && (
            <ErrorState>
              <EmptyTitle>Unable to load attendance</EmptyTitle>
              <EmptyText>{error}</EmptyText>
            </ErrorState>
          )}

          {!loading && !error && summary && (
            <>
              <SummaryGrid>
                <StatCard>
                  <StatLabel>Overall percentage</StatLabel>
                  <StatValue>{formatPercentage(summary.attendancePercentage)}</StatValue>
                  <StatHint>Target: 80% or higher recommended</StatHint>
                </StatCard>
                <StatCard>
                  <StatLabel>Attended classes</StatLabel>
                  <StatValue>{summary.attendedClasses || 0}</StatValue>
                  <StatHint>Out of {summary.totalClasses || 0} scheduled classes</StatHint>
                </StatCard>
                <StatCard>
                  <StatLabel>Absent classes</StatLabel>
                  <StatValue>{summary.absentClasses || 0}</StatValue>
                  <StatHint>Includes all recorded absences</StatHint>
                </StatCard>
              </SummaryGrid>

              <ProgressCard>
                <ProgressHeader>
                  <ProgressTitle>Attendance coverage</ProgressTitle>
                  <ProgressFigure>{formatPercentage(totalPercentage)}</ProgressFigure>
                </ProgressHeader>
                <ProgressTrack>
                  <ProgressFill $value={totalPercentage} />
                </ProgressTrack>
              </ProgressCard>
            </>
          )}

          {!loading && !error && !summary && (
            <EmptyState>
              <EmptyTitle>No attendance records found</EmptyTitle>
              <EmptyText>
                Once lecturers start marking attendance for this course, your overall statistics will appear here.
              </EmptyText>
            </EmptyState>
          )}
        </Section>

        <Section>
          <SectionHeading>
            <Title>Subject-wise attendance</Title>
            <Subtitle>Attendance percentages and class counts for each subject in this course.</Subtitle>
          </SectionHeading>

          {!loading && !error && subjects.length === 0 && (
            <EmptyState>
              <EmptyTitle>No subject-wise data yet</EmptyTitle>
              <EmptyText>
                Once attendance is recorded against class schedules, you'll see subject-wise percentages here.
              </EmptyText>
            </EmptyState>
          )}

          {!loading && !error && subjects.length > 0 && (
            <SubjectGrid>
              {subjects.map((subject) => {
                const statsById = subjectStatsFromSessions.get(String(subject.batchSubjectId));
                const nameKey = String(subject.subjectName || '')
                  .trim()
                  .toLowerCase();
                const statsByName = nameKey ? subjectStatsFromSessionsByName.get(nameKey) : null;
                const stats = statsById || statsByName;

                const derivedTotal = stats?.total ?? subject.totalClasses ?? 0;
                const derivedPresent = stats?.present ?? subject.attendedClasses ?? 0;
                const derivedAbsent = stats?.absent ?? subject.absentClasses ?? 0;

                const percentage = derivedTotal > 0
                  ? Math.round((derivedPresent / derivedTotal) * 1000) / 10
                  : (subject.attendancePercentage ?? 0);
                return (
                  <SubjectCard
                    key={subject.batchSubjectId}
                    onClick={() => setSelectedSubjectId(subject.batchSubjectId)}
                    className={String(subject.batchSubjectId) === String(selectedSubjectId) ? 'is-selected' : ''}
                  >
                    <SubjectHeader>
                      <div>
                        <SubjectTitle>{subject.subjectName}</SubjectTitle>
                        {subject.subjectShortName && <SubjectCode>{subject.subjectShortName}</SubjectCode>}
                      </div>
                      <SubjectPercentage>{formatPercentage(percentage)}</SubjectPercentage>
                    </SubjectHeader>
                    <SubjectMeta>
                      {derivedPresent} of {derivedTotal} classes attended
                    </SubjectMeta>
                    <BadgeRow>
                      <Badge>
                        Total classes: {derivedTotal}
                      </Badge>
                      <Badge>
                        Present: {derivedPresent}
                      </Badge>
                      <Badge>
                        Absent: {derivedAbsent}
                      </Badge>
                    </BadgeRow>
                  </SubjectCard>
                );
              })}
            </SubjectGrid>
          )}
        </Section>

        <Section>
          <SectionHeading>
            <Title>All attendance entries</Title>
            <Subtitle>
              Detailed list of your recorded attendance for this course, including dates, subjects, and status.
            </Subtitle>
          </SectionHeading>

          {!loading && !error && sessions.length === 0 && (
            <EmptyState>
              <EmptyTitle>No sessions recorded yet</EmptyTitle>
              <EmptyText>
                Once attendance is marked for your classes, each session will appear here.
              </EmptyText>
            </EmptyState>
          )}

          {!loading && !error && sessions.length > 0 && (
            <SessionsWrapper>
              <SessionsHeader>
                <span>{filteredSessions.length} session(s) found</span>
              </SessionsHeader>
              <FilterBar>
                <FilterChip
                  type="button"
                  $active={!subjectFilterId}
                  onClick={() => setSubjectFilterId(null)}
                >
                  <span>All subjects</span>
                  <span>· {sessions.length}</span>
                </FilterChip>
                {subjectFilterOptions.map((option) => (
                  <FilterChip
                    key={option.id}
                    type="button"
                    $active={String(subjectFilterId) === String(option.id)}
                    onClick={() => setSubjectFilterId(option.id)}
                  >
                    <span>{option.label}</span>
                    <span>· {option.count}</span>
                  </FilterChip>
                ))}
              </FilterBar>
              {groupedSessions.map((group) => (
                <div key={group.key}>
                  <DateHeading>{group.label}</DateHeading>
                  <SessionTable>
                    <SessionHeaderRow>
                      <div>Date</div>
                      <div>Subject</div>
                      <div>Hall / Room</div>
                      <div>Time</div>
                      <div>Lecturer</div>
                      <div>Status</div>
                    </SessionHeaderRow>
                    {group.items.map((session) => {
                      const palette = statusPalette[session.status] || statusPalette.Absent;
                      const timeLabel = [formatTime(session.startTime), formatTime(session.endTime)]
                        .filter(Boolean)
                        .join(' - ');
                      return (
                        <SessionRow key={session.attendanceId}>
                          <div>{formatDate(session.date)}</div>
                          <div>{session.subjectName || '-'}</div>
                          <div>{session.roomName || '-'}</div>
                          <div>{timeLabel || '-'}</div>
                          <div>{session.lecturerName || '-'}</div>
                          <div>
                            <StatusPill $bg={palette.bg} $color={palette.color}>
                              {palette.label}
                            </StatusPill>
                          </div>
                        </SessionRow>
                      );
                    })}
                  </SessionTable>
                </div>
              ))}
            </SessionsWrapper>
          )}
        </Section>
      </Wrapper>

      {!loading && !error && subjects.length > 0 && selectedSubject && (
        <ModalBackdrop onClick={() => setSelectedSubjectId(null)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalHeaderText>
                <ModalTitle>Subject-wise attendance details</ModalTitle>
                <ModalSubtitle>
                  {selectedSubject.subjectName}
                  {selectedSubject.subjectShortName ? ` · ${selectedSubject.subjectShortName}` : ''}
                  {selectedSubjectStats && (
                    <SubjectDetailCounts>
                      {selectedSubjectStats.present} present · {selectedSubjectStats.absent} absent · {selectedSubjectStats.total} total
                    </SubjectDetailCounts>
                  )}
                </ModalSubtitle>
              </ModalHeaderText>
              <ModalCloseButton onClick={() => setSelectedSubjectId(null)} aria-label="Close">
                ×
              </ModalCloseButton>
            </ModalHeader>

            <ModalBody>
              {selectedSubjectSessions.length === 0 ? (
                <EmptyText>
                  No detailed schedule data is available yet for this subject.
                </EmptyText>
              ) : (
                <SubjectDetailColumns>
                  <div>
                    {selectedSubjectGroupedSessions.map((group) => (
                      <div key={group.key}>
                        <DateHeading>{group.label}</DateHeading>
                        <SessionTable>
                          <SessionHeaderRow>
                            <div>Date</div>
                            <div>Subject</div>
                            <div>Hall / Room</div>
                            <div>Time</div>
                            <div>Lecturer</div>
                            <div>Status</div>
                          </SessionHeaderRow>
                          {group.items.map((session) => {
                            const palette = statusPalette[session.status] || statusPalette.Absent;
                            const timeLabel = [formatTime(session.startTime), formatTime(session.endTime)]
                              .filter(Boolean)
                              .join(' - ');
                            return (
                              <SessionRow key={`subject-${session.attendanceId}`}>
                                <div>{formatDate(session.date)}</div>
                                <div>{session.subjectName || '-'}</div>
                                <div>{session.roomName || '-'}</div>
                                <div>{timeLabel || '-'}</div>
                                <div>{session.lecturerName || '-'}</div>
                                <div>
                                  <StatusPill $bg={palette.bg} $color={palette.color}>
                                    {palette.label}
                                  </StatusPill>
                                </div>
                              </SessionRow>
                            );
                          })}
                        </SessionTable>
                      </div>
                    ))}
                  </div>
                </SubjectDetailColumns>
              )}
            </ModalBody>
          </ModalContainer>
        </ModalBackdrop>
      )}
    </StudentLayout>
  );
}
